import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { FaRegCalendarAlt, FaRegClock, FaStar } from "react-icons/fa";
import { HiOutlineVideoCamera } from "react-icons/hi";
import { fetchLatestBooking } from "actions/dataLoad";
import { useUserTokenSelector } from "store/reducers";
import "./index.css";

const formatSessionDate = (dateValue) => {
  const date = new Date(dateValue);
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
};

const formatSessionTime = (startValue, endValue) => {
  const start = new Date(startValue);
  const end = new Date(endValue);

  return `${start.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })} to ${end.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
  })}`;
};

const BIO_PREVIEW_LIMIT = 220;

const getLanguagesLabel = (languages = []) => {
  if (languages.length === 0) {
    return "";
  }

  if (languages.length === 1) {
    return languages[0];
  }

  if (languages.length === 2) {
    return `${languages[0]} & ${languages[1]}`;
  }

  if (languages.length === 3) {
    return `${languages[0]}, ${languages[1]} & ${languages[2]}`;
  }

  return `${languages[0]}, ${languages[1]}, ${languages[2]} & (+${languages.length - 3})`;
};

const MyTherapist = ({ showError }) => {
  const history = useHistory();
  const userToken = useUserTokenSelector();
  const showErrorRef = useRef(showError);
  const [bookingData, setBookingData] = useState(null);
  const [bookingType, setBookingType] = useState(null);
  const [isBioExpanded, setIsBioExpanded] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    showErrorRef.current = showError;
  }, [showError]);

  useEffect(() => {
    const loadLatestBooking = async () => {
      try {
        setLoading(true);
        const response = await fetchLatestBooking(userToken);
        if (response?.data?.upcoming?.orderID) {
          setBookingType("upcoming");
          setBookingData(response?.data?.upcoming);
        }
        else if (response?.data?.latest?.orderID) {
          setBookingType("latest");
          setBookingData(response?.data?.latest);
        }
        else {
          setBookingType(null);
          setBookingData(null);
        }
      } catch (error) {
        showErrorRef.current?.(
          error.response?.data?.message ||
            error.response?.data?.error ||
            "Failed to load therapist details."
        );
      } finally {
        setLoading(false);
      }
    };

    loadLatestBooking();
  }, [userToken]);

  useEffect(() => {
    setIsBioExpanded(false);
  }, [bookingData?._id]);

  const upcomingSessionState = useMemo(() => {
    const start = bookingData?.sessionTime?.start ? new Date(bookingData.sessionTime.start) : null;
    const end = bookingData?.sessionTime?.end ? new Date(bookingData.sessionTime.end) : null;
    const now = new Date();
    const timeDifference = start ? start.getTime() - now.getTime() : -Infinity;
    const timeDifferenceEnd = end ? end.getTime() - now.getTime() : -Infinity;

    return {
      hasUpcoming: Boolean(start && end && end >= now && bookingData?.isCancel !== true),
      canJoin: Boolean(
        start &&
          bookingData?.isCancel !== true &&
          bookingData?.meetid &&
          timeDifference <= 15 * 60 * 1000 &&
          timeDifferenceEnd >= 0
      ),
    };
  }, [bookingData]);

  if (loading) {
    return (
      <div className="my-therapist-page">
        <div className="my-therapist-empty">Loading therapist details...</div>
      </div>
    );
  }

  if (!bookingData?._id) {
    return (
      <div className="my-therapist-page">
        <div className="my-therapist-empty">No therapist assigned yet.</div>
      </div>
    );
  }

  const {
    _id,
    therapistID,
    meetid,
    therapistName,
    therapistImageUrl,
    therapistDesignation,
    therapistExperienceInYears,
    therapistExpertise = [],
    therapistApproach = [],
    therapistLanguages = [],
    therapistBio,
    therapistRating,
    therapistRatingsCount,
    therapistSessionsDoneCount,
    therapistTestimonials = [],
    sessionTime = {},
  } = bookingData;

  const visibleTags = therapistExpertise.slice(0, 3);
  const visibleTestimonials = therapistTestimonials.slice(0, 1);
  const hasLongBio = therapistBio && therapistBio.length > BIO_PREVIEW_LIMIT;
  const bioText =
    hasLongBio && !isBioExpanded
      ? `${therapistBio.slice(0, BIO_PREVIEW_LIMIT).trimEnd()}...`
      : therapistBio;
  const isLatestFallbackCard = bookingType === "latest" && !upcomingSessionState.hasUpcoming;

  const handleBookNow = () => {
    if (!therapistID) {
      history.push("/dashboard/sessionBooking");
      return;
    }

    history.push("/dashboard/sessionBooking", {
      fromMarketplace: true,
      marketplaceTherapist: {
        _id: therapistID,
        therapistName,
        therapistImageUrl,
        designation: therapistDesignation,
        experienceInYears: therapistExperienceInYears,
        specialities: therapistExpertise.map((problemCategory) => ({
          problemCategory,
        })),
        languages: therapistLanguages,
        description: therapistBio,
        ratings: {
          average: therapistRating || 0,
          count: therapistRatingsCount || 0,
        },
        sessionsDoneCount: therapistSessionsDoneCount || 0,
        testimonials: therapistTestimonials || [],
      },
    });
  };

  return (
    <div className="my-therapist-page">
      <div className="my-therapist-shell">
        <p className="my-therapist-heading">
          {upcomingSessionState.hasUpcoming ? "Upcoming Session" : "My Therapist"}
        </p>

        <div className="my-therapist-card">
          <div className="my-therapist-top">
            <div className="my-therapist-profile">
              <img
                src={therapistImageUrl || "/images/therapist-demo.png"}
                alt={therapistName || "Therapist"}
              />
              <div>
                <h2>{therapistName || "Therapist"}</h2>
                <p>
                  {therapistDesignation || "Therapist"} | {therapistExperienceInYears || 0} Yrs
                </p>
              </div>
            </div>

            {isLatestFallbackCard ? (
              <button
                type="button"
                className="my-therapist-join my-therapist-book-now"
                onClick={handleBookNow}
              >
                <span>Book Now</span>
              </button>
            ) : upcomingSessionState.canJoin ? (
              <Link
                to={{
                  pathname: `/session/${meetid}`,
                  state: { therapistID, bookingID: _id },
                }}
                className="my-therapist-join"
              >
                <span>Join Now</span>
                <HiOutlineVideoCamera size={20} />
              </Link>
            ) : (
              <button type="button" className="my-therapist-join disabled" disabled>
                <span>Join Now</span>
                <HiOutlineVideoCamera size={20} />
              </button>
            )}
          </div>

          {!isLatestFallbackCard && sessionTime?.start && sessionTime?.end ? (
            <div className="my-therapist-session-meta">
              <p>
                <FaRegCalendarAlt />
                {formatSessionDate(sessionTime.start)}
              </p>
              <p>
                <FaRegClock />
                {formatSessionTime(sessionTime.start, sessionTime.end)}
              </p>
            </div>
          ) : null}

          {visibleTags.length > 0 ? (
            <div className="my-therapist-tags">
              {visibleTags.map((tag) => (
                <span key={tag}>{tag}</span>
              ))}
            </div>
          ) : null}

          {therapistLanguages.length > 0 ? (
            <p className="my-therapist-languages">
              Fluent In {getLanguagesLabel(therapistLanguages)}
            </p>
          ) : null}

          {therapistBio ? (
            <div className="my-therapist-bio-block">
              <p className="my-therapist-bio">{bioText}</p>
              {hasLongBio ? (
                <button
                  type="button"
                  className="my-therapist-bio-toggle"
                  onClick={() => setIsBioExpanded((current) => !current)}
                >
                  {isBioExpanded ? "See less" : "See more"}
                </button>
              ) : null}
            </div>
          ) : null}

          <div className="my-therapist-stats">
            <div className="my-therapist-stat-card">
              <strong>
                <FaStar />
                {therapistRating ? therapistRating.toFixed(1) : "0.0"}
              </strong>
              <span>Rating</span>
            </div>
            <div className="my-therapist-stat-card">
              <strong>{therapistRatingsCount || 0}</strong>
              <span>Reviews</span>
            </div>
            <div className="my-therapist-stat-card">
              <strong>{therapistSessionsDoneCount || 0}+</strong>
              <span>Sessions</span>
            </div>
          </div>

          {therapistApproach.length > 0 ? (
            <div className="my-therapist-approach">
              <p>Approach</p>
              <ul>
                {therapistApproach.slice(0, 3).map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </div>
          ) : null}

          {visibleTestimonials.length > 0 ? (
            <div className="my-therapist-testimonials">
              <p className="my-therapist-testimonial-heading">Testimonials</p>
              <div className="my-therapist-testimonial-grid">
                {visibleTestimonials.map((item, index) => (
                  <div key={`${item.feedbackMessage}-${index}`} className="my-therapist-testimonial-card">
                    <div className="my-therapist-testimonial-stars">
                      {Array.from({ length: 5 }).map((_, starIndex) => (
                        <FaStar
                          key={starIndex}
                          className={starIndex < (item.rating || 0) ? "filled" : ""}
                        />
                      ))}
                    </div>
                    <p>{item.feedbackMessage}</p>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default MyTherapist;
